import React from "react";

const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "PRIVACY",
    intro:
      "Primenax respects your privacy. This page describes what information QCG Brands International, Inc. (\"Primenax\", \"we\", \"us\") collects from you on primenax.com, how we use it, and your rights.",
    sections: [
      {
        h: "Information We Collect",
        p: "Name, shipping address, email, phone, and payment details when you place an order; analytics data (pages viewed, device/browser type, IP); and email address when you subscribe to the newsletter or contact us.",
      },
      {
        h: "How We Use Your Information",
        p: "To fulfill and ship your orders (via Shippo/USPS/UPS/FedEx), process payment (via Stripe), respond to support inquiries, send order updates, and — only with your consent — marketing emails. We do not sell your data.",
      },
      {
        h: "Cookies & Analytics",
        p: "We use first-party cookies for cart persistence and analytics cookies to understand traffic. You can disable cookies in your browser; some site features (cart, checkout) require them.",
      },
      {
        h: "Your Rights",
        p: "You may request access, correction, or deletion of your personal data at any time by emailing contact@primenax.com. California residents have additional rights under CCPA; EU residents under GDPR. We respond within 30 days.",
      },
      {
        h: "Contact",
        p: "QCG Brands International, Inc., 1 Marina Park Dr, Suite 1410, Boston, MA 02210 · contact@primenax.com",
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    eyebrow: "TERMS",
    intro:
      "By accessing or purchasing from primenax.com you agree to these Terms. If you do not agree, do not use the site.",
    sections: [
      {
        h: "Eligibility",
        p: "You must be at least 18 years old to purchase Primenax products. By placing an order you affirm you are 18+ and a resident of a jurisdiction where these products are legal.",
      },
      {
        h: "Orders & Pricing",
        p: "All prices are in USD and may change without notice. We reserve the right to refuse or cancel any order due to suspected fraud, pricing errors, or stock issues. Confirmed orders ship within 1–3 business days.",
      },
      {
        h: "Intellectual Property",
        p: "Primenax®, Longevi-D™, Tenaci-T™, the winged-lion mark, and all site content are property of QCG Brands International, Inc. No reuse without written permission.",
      },
      {
        h: "Disclaimer of Warranties",
        p: "Products are sold \"as-is.\" Statements have not been evaluated by the FDA. Products are not intended to diagnose, treat, cure, or prevent any disease. Individual results vary.",
      },
      {
        h: "Limitation of Liability",
        p: "Primenax's total liability is limited to the amount paid for the product. We are not liable for indirect, incidental, or consequential damages.",
      },
      {
        h: "Governing Law",
        p: "These Terms are governed by the laws of the Commonwealth of Massachusetts, USA.",
      },
    ],
  },
  shipping: {
    title: "Shipping Policy",
    eyebrow: "SHIPPING",
    intro:
      "Primenax ships within the United States via USPS, UPS, and FedEx through our fulfillment partner Shippo. Real-time rates are calculated at checkout.",
    sections: [
      {
        h: "Processing Time",
        p: "Orders placed before 1pm ET ship the same business day. Orders after 1pm ET ship the next business day. Weekend and holiday orders ship the next business day.",
      },
      {
        h: "Delivery Estimates",
        p: "Ground (USPS / UPS Ground): 3–5 business days. Expedited (2-Day / Priority): 2–3 business days. Overnight (Next Day Air): 1 business day.",
      },
      {
        h: "Free Shipping",
        p: "US orders $75+ ship free via the cheapest ground option. Add items to your cart to reach the threshold.",
      },
      {
        h: "Discreet Packaging",
        p: "Every order ships in plain unmarked packaging. The Primenax brand is not visible from the outside.",
      },
      {
        h: "International",
        p: "International shipping is launching in phases. Join the newsletter to be notified when your country goes live.",
      },
      {
        h: "Lost or Damaged",
        p: "If your order arrives damaged or never arrives, contact contact@primenax.com within 14 days and we'll re-ship at no cost.",
      },
    ],
  },
  returns: {
    title: "Returns & 30-Day Guarantee",
    eyebrow: "RETURNS",
    intro:
      "Primenax stands behind every bottle with a 30-day money-back guarantee. If you don't feel the difference, you get your money back. No friction.",
    sections: [
      {
        h: "Eligibility",
        p: "Unopened or partially-used bottles are eligible for a full refund within 30 days of delivery. One refund per household per product.",
      },
      {
        h: "How to Request",
        p: "Email contact@primenax.com with your order number and the line: \"Refund request.\" We'll respond within one business day with a prepaid return label.",
      },
      {
        h: "Refund Timing",
        p: "Refunds are issued to the original payment method within 5–7 business days after we receive the returned bottle (or after we approve a no-return refund).",
      },
      {
        h: "Subscriptions",
        p: "You can cancel a Subscribe & Save plan at any time, with no penalty, by emailing contact@primenax.com or via your account page.",
      },
      {
        h: "Exclusions",
        p: "Bundles must be returned in full (both bottles). Items damaged by the customer after delivery are not eligible. Shipping fees are non-refundable.",
      },
    ],
  },
  disclaimer: {
    title: "Medical & FDA Disclaimer",
    eyebrow: "DISCLAIMER",
    intro:
      "Primenax products are dietary supplements. The information on this site is provided for general informational purposes only and does not constitute medical advice.",
    sections: [
      {
        h: "FDA Statement",
        p: "These statements have not been evaluated by the Food and Drug Administration. Primenax products are not intended to diagnose, treat, cure, or prevent any disease.",
      },
      {
        h: "Consult Your Doctor",
        p: "Consult a licensed healthcare professional before using any supplement, especially if you are pregnant, nursing, under 18, taking medication, or have a medical condition.",
      },
      {
        h: "Individual Results",
        p: "Customer testimonials and timeline claims (e.g., \"results within 4 weeks\") are illustrative and not guaranteed. Individual results vary based on age, lifestyle, training, diet, sleep, and genetics.",
      },
      {
        h: "Allergens",
        p: "Longevi-D contains shellfish (oyster extract). Check the supplement facts panel and contact us if you have concerns.",
      },
    ],
  },
};

export default function LegalPage({ doc }) {
  const data = LEGAL[doc];
  if (!data) return null;
  return (
    <div className="bg-[#050505] text-white" data-testid={`legal-${doc}-page`}>
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12 border-b border-white/10">
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          {data.eyebrow}
        </div>
        <h1
          className="font-display uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}
        >
          {data.title}
        </h1>
        <p className="mt-8 text-white/65 leading-relaxed max-w-3xl">
          {data.intro}
        </p>
        <p className="mt-3 text-white/35 text-xs">
          Last updated: {new Date().toLocaleString("en-US", { month: "long", year: "numeric" })}
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-16 md:py-20 space-y-12">
        {data.sections.map((s, i) => (
          <article key={i}>
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
              {String(i + 1).padStart(2, "0")}
            </div>
            <h2 className="font-display uppercase tracking-tight text-2xl md:text-3xl mb-4">
              {s.h}
            </h2>
            <p className="text-white/75 leading-relaxed">{s.p}</p>
          </article>
        ))}
      </section>
    </div>
  );
}
